import React from 'react';
import { Box, Flex, Text, Badge, Divider, Button, useToast } from '@chakra-ui/react';
import { EmergencyTicketSummary } from '../Types/types';
import styles from '../Subscription.module.css';
import apiConfig from '../../../apiConfig/apiConfig';

// Map a poco-order paymentStatus to a display badge
const refundStatusConfig: Record<string, { label: string; colorScheme: string }> = {
  refund_initiated: { label: 'Refund Initiated', colorScheme: 'orange' },
  partially_refunded: { label: 'Partially Refunded', colorScheme: 'yellow' },
  refunded: { label: 'Refunded', colorScheme: 'red' },
};

const RefundBadge = ({
  paymentStatus,
  refundedAmount,
}: {
  paymentStatus?: string | null;
  refundedAmount?: number;
}) => {
  if (!paymentStatus) return null;
  const config = refundStatusConfig[paymentStatus];
  if (!config) return null;
  const amountStr =
    refundedAmount && refundedAmount > 0
      ? ` ₹${refundedAmount.toLocaleString('en-IN')}`
      : '';
  return (
    <Badge colorScheme={config.colorScheme} fontSize="11px" px={2} py={0.5} borderRadius="4px" ml={1}>
      {config.label}{amountStr}
    </Badge>
  );
};

export const statusLabelMap: Record<string, string> = {
  'New': 'Active',
  'Work in progress': 'Active - In Progress',
  'Completed': 'Completed',
  'Cancelled': 'Cancelled',
};

export const getStatusColour = (status: string): string => {
  const map: Record<string, string> = {
    'New': 'yellow',
    'Work in progress': 'blue',
    'Completed': 'green',
    'Cancelled': 'red',
  };
  return map[status] ?? 'gray';
};

// Derive ticket type label from ticketType and Demo fields
const getTicketTypeLabel = (ticket: EmergencyTicketSummary): string => {
  if (ticket.Demo) return 'Demo';
  if (!ticket.ticketType) return 'Emergency';
  const map: Record<string, string> = {
    'emergencyTicket': 'Emergency',
    'TestDemo': 'Demo',
    'Customer Test': 'Demo',
    'Internal Test': 'Demo',
    'Insurance Hospital': 'Insurance Query',
    'Ambulance Booking': 'Ambulance Booking',
    'Pococare Service Query': 'Service Query',
  };
  return map[ticket.ticketType] ?? ticket.ticketType;
};

// Derive services provided from ambulanceInfo and doctorInfo presence
const getServicesProvided = (ticket: EmergencyTicketSummary): string => {
  const services: string[] = [];
  if (ticket.ambulanceInfo) services.push('Ambulance');
  if (ticket.doctorConsultationCompleted) services.push('Doctor Consultation');
  return services.length > 0 ? services.join(', ') : 'None';
};

interface EmergencyTicketCardProps {
  ticket: EmergencyTicketSummary;
}

export const EmergencyTicketCard = ({ ticket }: EmergencyTicketCardProps) => {
  const toast = useToast();
  const ticketId = `#${ticket._id}`;
  const statusLabel = statusLabelMap[ticket.ticketStatus] ?? ticket.ticketStatus;
  const statusColour = getStatusColour(ticket.ticketStatus);
  const patientName = ticket.patient?.fullName ?? 'Unknown patient';
  const ticketTypeLabel = getTicketTypeLabel(ticket);
  const servicesProvided = getServicesProvided(ticket);

  // Nature of emergency from currentMedicalStatusResponse
  const conditions = ticket.currentMedicalStatusResponse?.currentCondition ?? [];
  const otherDesc = ticket.currentMedicalStatusResponse?.otherDescription;
  const natureOfEmergency =
    conditions.length > 0
      ? [...conditions, otherDesc].filter(Boolean).join(', ')
      : otherDesc ?? 'Not specified';

  // Ambulance charges
  const hasAmbulanceCharges =
    ticket.ambulanceCharges?.paymentStatus === 'paid' &&
    ticket.ambulanceCharges?.amount != null;
  const ambulanceChargesDisplay = hasAmbulanceCharges
    ? `₹${ticket.ambulanceCharges!.amount!.toLocaleString('en-IN')}`
    : null;

  // Doctor charges
  const hasDoctorCharges = ticket.doctorCharges != null && ticket.doctorCharges > 0;
  const doctorChargesDisplay = hasDoctorCharges
    ? `₹${ticket.doctorCharges!.toLocaleString('en-IN')}`
    : null;

  // Invoice download URLs
  const paymentBaseUrl = `${apiConfig?.api?.baseURL ?? ''}/payment/api/v1`;
  const doctorInvoiceUrl = ticket.doctorOrderId
    ? `${paymentBaseUrl}/invoice/generate-invoice/${ticket.doctorOrderId}`
    : null;
  const ambulanceInvoiceUrl = ticket.ambulanceOrderId
    ? `${paymentBaseUrl}/invoice/generate-invoice/${ticket.ambulanceOrderId}`
    : null;
  const hasAnyInvoice = !!(doctorInvoiceUrl || ambulanceInvoiceUrl);

  const formattedDate = new Date(ticket.createdAt).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const downloadInvoice = async (url: string, filename: string) => {
    toast({
      title: 'Receipt (PDF)',
      description: 'Your receipt PDF is downloaded.',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
    try {
      const response = await fetch(url, { credentials: 'include' });
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback to window.open if fetch fails
      window.open(url, '_blank');
    }
  };

  return (
    <Box className={styles['ticket-card']}>
      {/* Header row: ticket ID, status badge, patient name */}
      <Flex className={styles['ticket-header']}>
        <Text className={styles['ticket-id']}>{ticketId}</Text>
        <Badge colorScheme={statusColour} fontSize="12px" px={2} py={1} borderRadius="4px">
          {statusLabel}
        </Badge>
        <Text className={styles['patient-name']}>
          <span className={styles['footer-label']}>Patient: </span>{patientName}
        </Text>
      </Flex>

      {/* Info grid */}
      <div className={styles['info-grid']}>
        {/* Nature of Emergency */}
        <div>
          <Text className={styles['info-col-label']}>Nature of Emergency</Text>
          <Text className={styles['info-col-value']}>{natureOfEmergency}</Text>
        </div>

        {/* Type */}
        <div>
          <Text className={styles['info-col-label']}>Type</Text>
          <Text className={styles['info-col-value']}>{ticketTypeLabel}</Text>
        </div>

        {/* Services Provided */}
        <div>
          <Text className={styles['info-col-label']}>Services Provided</Text>
          <Text className={styles['info-col-value']}>{servicesProvided}</Text>
        </div>

        {/* Charges — only shown if paid/refund state */}
        <div>
          <Text className={styles['info-col-label']}>Charges Paid</Text>
          {ambulanceChargesDisplay || doctorChargesDisplay ? (
            <Flex direction="column" gap={1} mt={1}>
              {ambulanceChargesDisplay && (
                <Flex alignItems="center" flexWrap="wrap" gap={1}>
                  <Text className={styles['info-col-value']} display="inline">
                    Ambulance: {ambulanceChargesDisplay}
                  </Text>
                  <RefundBadge
                    paymentStatus={ticket.ambulancePaymentStatus}
                    refundedAmount={ticket.ambulanceRefundedAmount}
                  />
                </Flex>
              )}
              {doctorChargesDisplay && (
                <Flex alignItems="center" flexWrap="wrap" gap={1}>
                  <Text className={styles['info-col-value']} display="inline">
                    Doctor: {doctorChargesDisplay}
                  </Text>
                  <RefundBadge
                    paymentStatus={ticket.doctorPaymentStatus}
                    refundedAmount={ticket.doctorRefundedAmount}
                  />
                </Flex>
              )}
            </Flex>
          ) : (
            <Text className={styles['info-col-value']}>N/A</Text>
          )}
        </div>
      </div>

      <Divider marginBottom="8px" />
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Flex gap={2} flexWrap="wrap">
          {ambulanceInvoiceUrl && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              onClick={() => downloadInvoice(ambulanceInvoiceUrl, `ambulance-invoice-${ticket._id}.pdf`)}
              className={styles['invoice-button']}
            >
              Ambulance Invoice
            </Button>
          )}
          {doctorInvoiceUrl && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              onClick={() => downloadInvoice(doctorInvoiceUrl, `doctor-invoice-${ticket._id}.pdf`)}
              className={styles['invoice-button']}
            >
              Doctor Invoice
            </Button>
          )}
          {!hasAnyInvoice && <Box />}
        </Flex>
        <Text className={styles['ticket-date']}>{formattedDate}</Text>
      </Flex>
    </Box>
  );
};
